import { messageForUser } from "@/utils/errorMapping";
import { formatDecimalString } from "@/utils/formatCurrency";
import { codigoDelError, estadoDelError } from "./errores";
import { datosDeCore } from "./respuestaDeCore";
import { elTopeNoDejaVender } from "./ventaSinRed";

/**
 * Los topes de la venta sin red del negocio (trd-client#346; §2.3 de
 * OFFLINE-POS-RONDA-2.md). `null` es sin tope, que es como arrancan. Llegan a
 * cada caja con el mandato del próximo latido. Los montos viajan como texto.
 */
export interface TopesSinConexion {
  /** El total de una venta sin red, con los recargos del medio de pago. */
  maxSaleAmount: string | null;
  /** Lo que suma una caja sin red con un mismo mandato, hasta que reconecta. */
  maxTotalAmount: string | null;
}

const monto = (valor: unknown): string | null => {
  if (typeof valor === "number" && Number.isFinite(valor)) return String(valor);
  return typeof valor === "string" && valor.trim() !== "" ? valor.trim() : null;
};

/** `GET /pos/settings`: `{ limits }`. `null` si la respuesta no los trae. */
export function topesDeLaRespuesta(cuerpo: unknown): TopesSinConexion | null {
  const limites = (datosDeCore(cuerpo) as { limits?: unknown } | null)
    ?.limits as Record<string, unknown> | null | undefined;
  if (!limites || typeof limites !== "object") return null;
  return {
    maxSaleAmount: monto(limites.maxSaleAmount),
    maxTotalAmount: monto(limites.maxTotalAmount),
  };
}

/** Del servidor al campo de la pantalla, que separa los decimales con coma. */
export function topeEnElCampo(valor: string | null): string {
  return valor === null ? "" : valor.replace(".", ",");
}

export type TopeDelCampo =
  | { ok: true; valor: string | null }
  | { ok: false; problema: string };

/**
 * Del campo al servidor: vacío es sin tope; si no, hasta 13 enteros y dos
 * decimales, lo mismo que acepta core.
 */
export function topeDelCampo(texto: string | null | undefined): TopeDelCampo {
  const limpio = (texto ?? "").trim();
  if (limpio === "") return { ok: true, valor: null };
  const valor = limpio.replace(",", ".");
  const sinCeros = valor.replace(/^0+(?=\d)/, "");
  if (!/^\d{1,13}(\.\d{1,2})?$/.test(sinCeros)) {
    return {
      ok: false,
      problema: "Escribí un monto, con hasta dos decimales.",
    };
  }
  return { ok: true, valor: sinCeros };
}

/** Cómo se lee un tope en la pantalla del dueño. */
export function textoDelTope(
  valor: string | null,
  cual: "por-venta" | "acumulado"
): string {
  if (valor === null) return "Sin tope";
  if (cual === "por-venta" && elTopeNoDejaVender(valor)) {
    return "No vende sin conexión";
  }
  return formatDecimalString(valor);
}

export function motivoAlGuardarLosTopes(error: unknown): string {
  if (codigoDelError(error) === "business_suspended") {
    return "El negocio está suspendido: no se pueden cambiar los topes.";
  }
  if (estadoDelError(error) === 403) {
    return "Para cambiar los topes hace falta el permiso de Depósitos.";
  }
  return messageForUser(error, "No se pudieron guardar los topes.");
}
